import ShopNavBar from '../../components/ShopNavBar'
import { useAuth } from '../../contexts/AuthContext';
import { useEffect, useMemo, useState } from 'react';
import { usersDbRef } from '../../utils/dbRefs';
import { get } from 'firebase/database';
import AdminItemShops from '../../components/admin/AdminItemShops';
import AdminItemShopsShimmer from '../../components/admin/AdminItemShopsShimmer';
import { auth } from '../../firebase/firebaseConfig';
import { functionsBaseUrl } from '../../config/runtime';

const AdminViewAllShops = () => {
    const { currentUser } = useAuth();
    const [activeFilter, setActiveFilter] = useState("all");
    const [openShopId, setOpenShopId] = useState(null);
    const [shops, setShops] = useState([]);
    const [loading, setLoading] = useState(true);


    useEffect(() => {
        const fetchShops = async () => {
            if (!currentUser?.uid) return;

            setLoading(true);

            try {
                const snapshot = await get(usersDbRef);

                if (!snapshot.exists()) {
                    setShops([]);
                    return;
                }

                const data = snapshot.val();

                const list = Object.entries(data)
                    .map(([id, value]) => ({
                        id,
                        ...value,
                    }))
                    .filter((item) => item.type !== "admin")
                    .sort((a, b) =>
                        b.createdAt - a.createdAt
                    );

                setShops(list);
            } catch (error) {
                console.error("Error loading shops:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchShops();
    }, [currentUser?.uid]);

    const filteredShops = useMemo(() => {
        if (activeFilter === "all") return shops;

        if (activeFilter === "review") {
            return shops.filter((e) => e.status === "review");
        }

        if (activeFilter === "approved") {
            return shops.filter(
                (e) => e.status === "approved"
            );
        }

        if (activeFilter === "rejected") {
            return shops.filter((e) => e.status === "rejected");
        }

        return shops;
    }, [activeFilter, shops]);

    const reviewCount = shops.filter((e) => e.status === "review").length;
    const approvedCount = shops.filter((e) => e.status === "approved").length;
    const rejectedCount = shops.filter((e) => e.status === "rejected").length;


    const handleToggleRead = (shop) => {
        const isOpening = openShopId !== shop.id;
        setOpenShopId(isOpening ? shop.id : null);
    };

    const updateStatus = async (status, uid, email, item) => {
        try {
            const token = await auth.currentUser.getIdToken(true);
            const response = await fetch(`${functionsBaseUrl}/updateListingStatus`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ status, uid }),
            });
            const result = await response.json();
            if (!response.ok || result.error) throw new Error(result.error || "Status update failed");
            setShops((prev) =>
                prev.map((e) =>
                    e.id === item.id ? { ...e, status: status } : e
                )
            );
        } catch (err) {
            console.error("Status update failed:", err);
            alert(err.message || "Status update failed. The listing was not changed.");
        }
    };

    return (
        <div className='bg-background min-h-full flex flex-col font-["Plus_Jakarta_Sans",sans-serif]'>
            <ShopNavBar type={currentUser?.type || 'shop'} />
            <div className='flex-1 max-w-[1200px] mx-auto w-full px-[16px] md:px-[40px] py-[24px] md:py-[32px] pb-[80px] md:pb-[40px]'>
                <div className="mb-[24px]">
                    <h1 className="font-semibold text-[24px] md:text-[28px] text-[#212326] mb-[4px]">
                        All Shops
                    </h1>
                    <p className="font-medium text-[14px] text-[#717182]">
                        List of all shops registered on SignetPrint.
                    </p>
                </div>

                <div className="flex gap-[8px] mb-[20px] flex-wrap">

                    {/* ALL */}
                    <button
                        onClick={() => setActiveFilter("all")}
                        className={`px-[14px] py-[7px] rounded-[10px] font-semibold text-[13px] transition-all ${activeFilter === "all"
                            ? "bg-[#ff5323] text-white"
                            : "bg-white border border-[#e4e5e7] text-[#717182] hover:border-[#c0c2c6]"
                            }`}
                    >
                        All
                    </button>

                    {/* REVIEW */}
                    <button
                        onClick={() => setActiveFilter("review")}
                        className={`px-[14px] py-[7px] rounded-[10px] font-semibold text-[13px] transition-all ${activeFilter === "review"
                            ? "bg-[#ff5323] text-white"
                            : "bg-white border border-[#e4e5e7] text-[#717182] hover:border-[#c0c2c6]"
                            }`}
                    >
                        Review
                        <span className="ml-[6px] inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-white/20 text-[10px]">
                            {reviewCount}
                        </span>
                    </button>

                    {/* APPROVED */}
                    <button
                        onClick={() => setActiveFilter("approved")}
                        className={`px-[14px] py-[7px] rounded-[10px] font-semibold text-[13px] transition-all ${activeFilter === "approved"
                            ? "bg-[#ff5323] text-white"
                            : "bg-white border border-[#e4e5e7] text-[#717182] hover:border-[#c0c2c6]"
                            }`}
                    >
                        Approved
                        <span className="ml-[6px] inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-white/20 text-[10px]">
                            {approvedCount}
                        </span>
                    </button>

                    {/* REJECTED */}
                    <button
                        onClick={() => setActiveFilter("rejected")}
                        className={`px-[14px] py-[7px] rounded-[10px] font-semibold text-[13px] transition-all ${activeFilter === "rejected"
                            ? "bg-[#ff5323] text-white"
                            : "bg-white border border-[#e4e5e7] text-[#717182] hover:border-[#c0c2c6]"
                            }`}
                    >
                        Rejected
                        <span className="ml-[6px] inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-white/20 text-[10px]">
                            {rejectedCount}
                        </span>
                    </button>
                </div>

                <div className="bg-white rounded-[16px] border border-[#e4e5e7] overflow-hidden">

                    {/* LOADING */}
                    {loading &&
                        Array.from({ length: 3 }).map((_, i) => (
                            <AdminItemShopsShimmer key={i} />
                        ))
                    }

                    {/* DATA */}
                    {!loading && filteredShops.length > 0 &&
                        <div className="divide-y divide-[#f0f1f3]">
                            {filteredShops.map((item) => (
                                <AdminItemShops
                                    key={item.id}
                                    item={item}
                                    isOpen={openShopId === item.id}
                                    onToggle={() => handleToggleRead(item)}
                                    updateStatus={updateStatus}
                                />
                            ))}
                        </div>
                    }

                    {/* EMPTY STATE */}
                    {!loading && filteredShops.length === 0 && (
                        <div className="py-[40px] text-center">
                            <p className="text-[13px] text-[#9298a0] font-semibold">
                                No shops found
                            </p>
                        </div>
                    )}



                </div>

            </div>
        </div>

    )
}

export default AdminViewAllShops;
